import data from '../../data/namesTier0.json';
import { getZodiac, getStarSign, getEraLabel } from './derive';
import { selectFromDerived } from './selectName';
import {
  renderFrontCard,
  renderBackCard,
  toPreviewDataUrl,
} from './renderCard';
import { preloadCardAssets, countCardAssets } from './knameAssets';

// 에셋 로드 뒤에 남는 작업 수 — 앞장 렌더, 뒷장 렌더, 미리보기 dataURL 생성
const RENDER_STEPS = 3;

let cached = null;

const toDerived = (input) =>
  input.zodiacId
    ? input
    : {
        gender: input.gender,
        mood: input.mood,
        zodiacId: getZodiac(input.birthYear),
        backgroundId: getStarSign(input.birthMonth, input.birthDay),
        eraLabel: getEraLabel(input.birthYear),
        retryCount: input.retryCount ?? 0,
        givenName: input.givenName,
      };

const makeKey = (d) =>
  [
    d.gender,
    d.mood,
    d.zodiacId,
    d.backgroundId,
    d.eraLabel,
    d.retryCount ?? 0,
  ].join('|');

/**
 * 단조 증가하는 진행률 스토어.
 * 구독 즉시 현재 스냅샷을 한 번 전달하므로, 늦게 붙은 구독자도
 * 이미 끝난 단계를 반영한 값부터 받는다.
 */
const createProgress = (total) => {
  let loaded = 0;
  let phase = 'assets';
  const subscribers = new Set();

  const snapshot = () => ({
    loaded,
    total,
    phase,
    ratio: total > 0 ? Math.min(1, loaded / total) : 0,
  });

  const emit = () => {
    const snap = snapshot();
    subscribers.forEach((fn) => {
      try {
        fn(snap);
      } catch (e) {
        console.error('progress subscriber failed:', e);
      }
    });
  };

  return {
    snapshot,
    step(nextPhase) {
      loaded = Math.min(total, loaded + 1);
      if (nextPhase) phase = nextPhase;
      emit();
    },
    setPhase(next) {
      phase = next;
      emit();
    },
    subscribe(fn) {
      subscribers.add(fn);
      fn(snapshot());
      return () => subscribers.delete(fn);
    },
  };
};

const buildEntry = (input) => {
  const derived = toDerived(input);
  const selection = selectFromDerived(
    data.names,
    data.candidate_data_version,
    derived,
  );
  const progress = createProgress(countCardAssets(selection) + RENDER_STEPS);

  const promise = (async () => {
    // 1단계 — 에셋 확보. 이미 프리로드됐다면 즉시 통과한다.
    await preloadCardAssets(selection, () => progress.step());

    // 2단계 — canvas 렌더
    progress.setPhase('render');
    const frontCanvas = await renderFrontCard({
      backgroundId: selection.backgroundId,
      zodiacId: selection.zodiacId,
      hangul: selection.name.hangul,
    });
    progress.step();

    const backCanvas = await renderBackCard({
      name: selection.name,
      zodiacId: selection.zodiacId,
      backgroundId: selection.backgroundId,
    });
    progress.step();

    // 3단계 — 미리보기 dataURL
    const frontUrl = toPreviewDataUrl(frontCanvas);
    const backUrl = toPreviewDataUrl(backCanvas);
    progress.step('done');

    // 공유용 합성 이미지는 무겁다(toDataURL). 실제로 공유할 때까지 미룬다.
    let combinedUrl = null;
    const getCombinedShareUrl = () => {
      if (combinedUrl) return combinedUrl;
      const GAP = 12;
      const combined = document.createElement('canvas');
      combined.width = 480 * 2 + GAP;
      combined.height = 720;
      const cctx = combined.getContext('2d');
      cctx.fillStyle = '#17111d';
      cctx.fillRect(0, 0, combined.width, combined.height);
      cctx.drawImage(frontCanvas, 0, 0, 480, 720);
      cctx.drawImage(backCanvas, 480 + GAP, 0, 480, 720);
      combinedUrl = combined.toDataURL('image/png');
      return combinedUrl;
    };

    return { selection, derived, frontUrl, backUrl, getCombinedShareUrl };
  })();

  return { key: makeKey(derived), promise, progress };
};

const getEntry = (input) => {
  const derived = toDerived(input);
  const key = makeKey(derived);
  if (cached?.key === key) return cached;
  cached = buildEntry(input);
  return cached;
};

/**
 * 무드와 무관하게 항상 쓰이는 공용 에셋(폰트·명패·뒷장 베이스)만 미리 받는다.
 * 무드 선택 화면 진입 즉시 호출한다.
 */
export const warmSharedAssets = () => preloadCardAssets({});

/**
 * 특정 무드로 확정될 경우 필요한 에셋을 미리 받는다. (canvas 렌더는 하지 않는다)
 * 무드 카드를 누르는 순간 호출하면 네트워크가 한발 앞서 돈다.
 */
export const warmAssetsForMood = (input) => {
  try {
    const derived = toDerived(input);
    const selection = selectFromDerived(
      data.names,
      data.candidate_data_version,
      derived,
    );
    return preloadCardAssets(selection);
  } catch (e) {
    // 선택 로직이 실패해도 프리로드는 부가 기능이므로 조용히 넘어간다.
    return Promise.resolve();
  }
};

export const prefetchResult = (input) => getEntry(input).promise;

/**
 * @param {object} input
 * @param {(p:{loaded:number,total:number,ratio:number,phase:string})=>void} [onProgress]
 * @returns {Promise} 렌더 결과
 */
export const getResult = (input, onProgress) => {
  const entry = getEntry(input);
  if (onProgress) {
    const unsubscribe = entry.progress.subscribe(onProgress);
    entry.promise.then(unsubscribe, unsubscribe);
  }
  return entry.promise;
};
